import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { normalizeProgramEnrollmentRows } from '../../utils/dataNormalizer';

const PROGRAM_REFRESH_THROTTLE_MS = Number(import.meta.env?.VITE_PROGRAM_REFRESH_THROTTLE_MS || 2000);

const useSoftRefreshQueue = ({ isSoftRefreshMode }) => {
  const queryClient = useQueryClient();
  const [softRefreshSnackbar, setSoftRefreshSnackbar] = useState({ open: false, message: '' });
  const pendingProgramRefreshesRef = useRef(new Set());
  const refreshApplyTimerRef = useRef(null);
  const refreshApplyInFlightRef = useRef(false);
  const forceApplyRef = useRef(false);
  const softRefreshModeRef = useRef(false);

  const fetchAndApplyProgram = useCallback(
    async (programName) => {
      if (!programName) return;
      const { data } = await axios.get(`/api/enrollment-report/program/${encodeURIComponent(programName)}`);
      const normalized = normalizeProgramEnrollmentRows(data);
      queryClient.setQueryData(['enrollmentData', { type: 'program', value: programName }], normalized);
      const existing = queryClient.getQueryData(['allEnrollmentData']);
      if (Array.isArray(existing)) {
        const updated = existing.some((entry) => entry.programName === programName)
          ? existing.map((entry) => (entry.programName === programName ? { ...entry, data: normalized } : entry))
          : [...existing, { programName, data: normalized }];
        queryClient.setQueryData(['allEnrollmentData'], updated);
      }
    },
    [queryClient]
  );

  const flushPendingRefreshes = useCallback(
    async (options = {}) => {
      const force = Boolean(options.force);
      if (softRefreshModeRef.current && !force) return;
      if (refreshApplyInFlightRef.current) {
        if (force && !refreshApplyTimerRef.current) {
          forceApplyRef.current = true;
          refreshApplyTimerRef.current = setTimeout(() => {
            refreshApplyTimerRef.current = null;
            flushPendingRefreshes({ force: true });
          }, 0);
        }
        return;
      }
      const pending = Array.from(pendingProgramRefreshesRef.current);
      if (pending.length === 0) return;
      pendingProgramRefreshesRef.current.clear();
      refreshApplyInFlightRef.current = true;
      try {
        await Promise.all(
          pending.map(async (programName) => {
            try {
              await fetchAndApplyProgram(programName);
            } catch (err) {
              console.error('Failed to refresh program cache after realtime update:', err);
            }
          })
        );
      } finally {
        refreshApplyInFlightRef.current = false;
        if (pendingProgramRefreshesRef.current.size === 0) {
          forceApplyRef.current = false;
        }
        const allowApply = forceApplyRef.current || !softRefreshModeRef.current;
        if (pendingProgramRefreshesRef.current.size > 0 && allowApply && !refreshApplyTimerRef.current) {
          refreshApplyTimerRef.current = setTimeout(() => {
            refreshApplyTimerRef.current = null;
            flushPendingRefreshes({ force: forceApplyRef.current });
          }, 0);
        }
      }
    },
    [fetchAndApplyProgram]
  );

  const scheduleRefreshApply = useCallback(
    (delayMs = PROGRAM_REFRESH_THROTTLE_MS) => {
      if (refreshApplyTimerRef.current) return;
      refreshApplyTimerRef.current = setTimeout(() => {
        refreshApplyTimerRef.current = null;
        flushPendingRefreshes();
      }, Math.max(0, delayMs));
    },
    [flushPendingRefreshes]
  );

  const queueProgramRefresh = useCallback(
    (programName) => {
      if (!programName) return;
      pendingProgramRefreshesRef.current.add(programName);
      if (softRefreshModeRef.current) {
        setSoftRefreshSnackbar({
          open: true,
          message: 'New data available - refresh view.',
        });
        return;
      }
      scheduleRefreshApply();
    },
    [scheduleRefreshApply]
  );

  const handleSoftRefreshApply = useCallback(() => {
    setSoftRefreshSnackbar((prev) => (prev.open ? { ...prev, open: false } : prev));
    forceApplyRef.current = true;
    flushPendingRefreshes({ force: true });
  }, [flushPendingRefreshes]);

  useEffect(() => {
    softRefreshModeRef.current = isSoftRefreshMode;
    if (!isSoftRefreshMode && pendingProgramRefreshesRef.current.size > 0) {
      scheduleRefreshApply(0);
    }
    if (!isSoftRefreshMode) {
      setSoftRefreshSnackbar((prev) => (prev.open ? { ...prev, open: false } : prev));
    }
  }, [isSoftRefreshMode, scheduleRefreshApply]);

  useEffect(() => {
    return () => {
      if (refreshApplyTimerRef.current) {
        clearTimeout(refreshApplyTimerRef.current);
      }
    };
  }, []);

  const closeSoftRefreshSnackbar = useCallback(() => {
    setSoftRefreshSnackbar((prev) => (prev.open ? { ...prev, open: false } : prev));
  }, []);

  return {
    softRefreshSnackbar,
    queueProgramRefresh,
    handleSoftRefreshApply,
    closeSoftRefreshSnackbar,
  };
};

export default useSoftRefreshQueue;
